import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from './context/AuthContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { ThemeProvider } from './context/ThemeContext';
import CookieConsent from './components/CookieConsent';

// Eager load critical pages
import Landing from './pages/Landing';
import Auth from './pages/Auth';
import GoogleCallback from './pages/GoogleCallback';
import GitHubCallback from './pages/GitHubCallback';
import InvitationHandler from './pages/InvitationHandler';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './pages/Dashboard'; // Eager load Dashboard for instant navigation
import Board from './pages/Board'; // Eager load Board for instant navigation
import { Toaster } from './components/ui/toaster';
import DashboardSkeleton from './components/skeletons/DashboardSkeleton';
import './App.css';

// Lazy load non-critical pages for better initial load performance
// Dashboard and Board are now eager loaded above for instant navigation
const Pricing = lazy(() => import('./pages/Pricing'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const ChatNew = lazy(() => import('./pages/ChatNew'));
const Documentation = lazy(() => import('./pages/Documentation'));
const PublicBoard = lazy(() => import('./pages/PublicBoard'));
const Contacts = lazy(() => import('./pages/Contacts'));
const Support = lazy(() => import('./pages/Support'));
const Privacy = lazy(() => import('./pages/Privacy'));
const SuperAdminPanel = lazy(() => import('./pages/SuperAdminPanel'));
const Settings = lazy(() => import('./pages/Settings'));

// Create a QueryClient instance with optimized settings for faster perceived performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // Data stays fresh for 2 minutes (reduced from 5)
      cacheTime: 5 * 60 * 1000, // Cache kept for 5 minutes (reduced from 10)
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnMount: false, // Don't refetch on component mount if data is fresh
      retry: 0, // No retries for faster perceived performance
      suspense: false, // Disable suspense for faster rendering
    },
    mutations: {
      retry: 0, // No retries for faster perceived performance
    },
  },
});

function App() {
  return (
    <div className="App">
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <BrowserRouter>
            <AuthProvider>
              <SubscriptionProvider>
          <Routes>
            {/* Eager loaded routes - no Suspense needed */}
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/github/callback" element={<GitHubCallback />} />
            <Route path="/auth/google/callback" element={<GoogleCallback />} />
            <Route path="/invitation/accept" element={<InvitationHandler />} />
            <Route path="/invitation/decline" element={<InvitationHandler />} />
            
            {/* Lazy loaded routes - wrapped in Suspense */}
            <Route
              path="/documentation"
              element={
                <Suspense fallback={<DashboardSkeleton />}>
                  <Documentation />
                </Suspense>
              }
            />
            <Route
              path="/support"
              element={
                <Suspense fallback={<DashboardSkeleton />}>
                  <Support />
                </Suspense>
              }
            />
            <Route
              path="/privacy"
              element={
                <Suspense fallback={<DashboardSkeleton />}>
                  <Privacy />
                </Suspense>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<DashboardSkeleton />}>
                    <Settings />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/super-admin"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<DashboardSkeleton />}>
                    <SuperAdminPanel />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<DashboardSkeleton />}>
                    <Onboarding />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/board"
              element={
                <ProtectedRoute>
                  <Board />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<DashboardSkeleton />}>
                    <ChatNew />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pricing"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<DashboardSkeleton />}>
                    <Pricing />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* Public share link — no auth required */}
            <Route
              path="/share/:token"
              element={
                <Suspense fallback={<DashboardSkeleton />}>
                  <PublicBoard />
                </Suspense>
              }
            />
            {/* Contacts / CRM */}
            <Route
              path="/contacts"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<DashboardSkeleton />}>
                    <Contacts />
                  </Suspense>
                </ProtectedRoute>
              }
            />
          </Routes>
          <Toaster />
          <CookieConsent />
          </SubscriptionProvider>
        </AuthProvider>
      </BrowserRouter>
        </ThemeProvider>
      {/* React Query Devtools - only in development - Temporarily disabled */}
      {/* {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
      )} */}
    </QueryClientProvider>
    </div>
  );
}

export default App;